import React, { useState } from 'react';
import { ChevronLeft, Trash2, LogOut, Crown, UserMinus } from 'lucide-react';
import ApiService from '../services/ApiService';

function FamilyProfileView({
  family,
  relatives,
  currentUserUuid,
  onBack,
  onShowToast,
  onFamilyUpdated,
}) {
  const [editingName, setEditingName] = useState(false);
  const [familyName, setFamilyName] = useState(family.name);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);
  const [editingRelationship, setEditingRelationship] = useState(null);

  const isOwner = family.role === 'owner';

  // Get members of THIS family from the full relatives array
  const enrichedMembers = relatives
    .filter(r => (r.families || []).some(f => f.uuid === family.uuid))
    .map(r => {
      const mf = r.families.find(f => f.uuid === family.uuid);
      const status = r.type !== 'phoneEnabled'
        ? 'Passive'
        : mf.approvalStatus === 'pending' ? 'Invited' : 'Active';
      return {
        ...r,
        familyRole: mf.role,
        memberStatus: status,
        isCurrentUser: r.uuid === currentUserUuid,
      };
    });

  const ownerCount = enrichedMembers.filter(m => m.familyRole === 'owner').length;
  const isSoleOwner = isOwner && ownerCount === 1;
  const canDeleteFamily = isOwner && enrichedMembers.length === 1;

  const getInitial = (name) => name ? name[0].toUpperCase() : '?';
  const getProfileImage = (member) =>
    member.profile?.profilePictureUrl || member.profilePictureUrl || null;

  const statusStyle = (status) => {
    if (status === 'Active') return 'bg-green-600/20 text-green-400';
    if (status === 'Invited') return 'bg-yellow-600/20 text-yellow-400';
    return 'bg-gray-600/20 text-gray-400';
  };

  const relationshipOptions = [
    'parent', 'father', 'mother', 'child', 'son', 'daughter',
    'sibling', 'brother', 'sister',
    'friend', 'spouse', 'husband', 'wife', 'partner',
    'significantOther', 'domesticPartner',
    'grandparent', 'grandfather', 'grandmother',
    'greatGrandparent', 'greatGrandfather', 'greatGrandmother',
    'grandchild', 'grandson', 'granddaughter',
    'greatGrandchild', 'greatGrandson', 'greatGranddaughter',
    'aunt', 'uncle', 'niece', 'nephew', 'cousin', 'firstCousin', 'secondCousin',
    'sonInLaw', 'daughterInLaw', 'brotherInLaw', 'sisterInLaw',
    'stepparent', 'stepmother', 'stepfather', 'stepson', 'stepdaughter',
    'stepsibling', 'stepbrother', 'stepsister',
    'guardian', 'other',
  ];

  const formatRelationship = (r) =>
    r.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());

  // ── Handlers ──

  const handleSaveFamilyName = async () => {
    if (!familyName.trim() || familyName.trim() === family.name) {
      setEditingName(false);
      return;
    }
    setSaving(true);
    try {
      await ApiService.updateFamilyProperties(family.uuid, {
        familyUpdates: { name: familyName.trim() },
      });
      onShowToast?.('Family name updated', 'success');
      setEditingName(false);
      if (onFamilyUpdated) await onFamilyUpdated();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to update family name', 'error');
    }
    setSaving(false);
  };

  const handleChangeRelationship = async (memberUuid, newRelationship) => {
    setSaving(true);
    try {
      await ApiService.updateUserProperties({
        action: 'update',
        relativeUpdates: [{ uuid: memberUuid, relationship: newRelationship }]
      });
      onShowToast?.('Relationship updated', 'success');
      setEditingRelationship(null);
      if (onFamilyUpdated) await onFamilyUpdated();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to update relationship', 'error');
    }
    setSaving(false);
  };

  const handleToggleMemberRole = async (member) => {
    const newRole = member.familyRole === 'owner' ? 'member' : 'owner';
    if (member.isCurrentUser && member.familyRole === 'owner' && isSoleOwner) {
      onShowToast?.('Another user must be made an owner before you can remove your ownership.', 'error');
      setRoleChangeTarget(null);
      return;
    }
    setSaving(true);
    try {
      await ApiService.updateFamilyProperties(family.uuid, {
        memberUpdates: [{ uuid: member.uuid, role: newRole }],
      });
      onShowToast?.(`${member.name} is now ${newRole === 'owner' ? 'an owner' : 'a member'}`, 'success');
      setRoleChangeTarget(null);
      if (onFamilyUpdated) await onFamilyUpdated();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to update role', 'error');
    }
    setSaving(false);
  };

  const handleRemoveMember = async (member) => {
    setSaving(true);
    try {
      await ApiService.removeFamilyMember(family.uuid, member.uuid);
      onShowToast?.(`${member.name} removed from family`, 'success');
      setRemoveMemberTarget(null);
      if (onFamilyUpdated) await onFamilyUpdated();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to remove member', 'error');
    }
    setSaving(false);
  };

  const handleLeaveFamily = async () => {
    if (isSoleOwner && enrichedMembers.length > 1) {
      onShowToast?.('Another user must be made an owner before you can remove yourself from this family.', 'error');
      setShowLeaveConfirm(false);
      return;
    }
    setSaving(true);
    try {
      await ApiService.removeFamilyMember(family.uuid, currentUserUuid);
      onShowToast?.('You have left the family', 'success');
      if (onFamilyUpdated) await onFamilyUpdated();
      onBack();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to leave family', 'error');
    }
    setSaving(false);
  };

  const handleDeleteFamily = async () => {
    setSaving(true);
    try {
      await ApiService.deleteFamily(family.uuid);
      onShowToast?.('Family deleted', 'success');
      if (onFamilyUpdated) await onFamilyUpdated();
      onBack();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to delete family', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center gap-3 sticky top-0 z-40 shadow-xl">
        <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Family Profile</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Family Header Card */}
        <div className="bg-gray-800 rounded-xl p-6 flex flex-col items-center">
          {/* Overlapping avatars */}
          <div className="flex items-center justify-center mb-4">
            {enrichedMembers.slice(0, 6).map((member, idx) => {
              const img = getProfileImage(member);
              return (
                <div
                  key={member.uuid}
                  className={`w-14 h-14 rounded-full border-2 border-gray-800 flex items-center justify-center overflow-hidden ${idx > 0 ? '-ml-3' : ''}`}
                  style={{ zIndex: enrichedMembers.length - idx }}
                >
                  {img ? (
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-600 flex items-center justify-center">
                      <span className="text-sm font-semibold text-gray-300">{getInitial(member.name)}</span>
                    </div>
                  )}
                </div>
              );
            })}
            {enrichedMembers.length > 6 && (
              <div className="-ml-3 w-14 h-14 rounded-full border-2 border-gray-800 bg-gray-700 flex items-center justify-center">
                <span className="text-xs font-semibold text-gray-300">+{enrichedMembers.length - 6}</span>
              </div>
            )}
          </div>

          {/* Family name */}
          {editingName && isOwner ? (
            <div className="flex gap-2 w-full max-w-xs">
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg text-center focus:outline-none focus:ring-1 focus:ring-purple-500"
                autoFocus
              />
              <button
                onClick={handleSaveFamilyName}
                disabled={saving || !familyName.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                Save
              </button>
              <button
                onClick={() => { setFamilyName(family.name); setEditingName(false); }}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold">{familyName}</h2>
              {isOwner && (
                <button
                  onClick={() => { setFamilyName(family.name); setEditingName(true); }}
                  className="text-sm text-purple-400 hover:text-purple-300 mt-1 transition-colors"
                >
                  Edit Name
                </button>
              )}
            </>
          )}
          <p className="text-sm text-gray-400 mt-1">{enrichedMembers.length} member{enrichedMembers.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Members List */}
        <div className="bg-gray-800 rounded-xl p-4">
          <label className="text-sm text-gray-400 mb-3 block">Members</label>
          <div className="divide-y divide-gray-700">
            {enrichedMembers.map((member) => {
              const img = getProfileImage(member);
              return (
                <div key={member.uuid} className="flex items-center gap-3 py-3">
                  {/* Avatar */}
                  {img ? (
                    <img src={img} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-gray-300">{getInitial(member.name)}</span>
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {member.name}
                      {member.isCurrentUser && <span className="text-gray-500 ml-1">(You)</span>}
                    </p>
                    {editingRelationship === member.uuid ? (
                      <select
                        value={member.relationship || ''}
                        onChange={(e) => handleChangeRelationship(member.uuid, e.target.value)}
                        onBlur={() => setEditingRelationship(null)}
                        autoFocus
                        disabled={saving}
                        className="mt-1 bg-gray-700 text-white text-xs px-2 py-1 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        <option value="">Select...</option>
                        {relationshipOptions.map(r => (
                          <option key={r} value={r}>{formatRelationship(r)}</option>
                        ))}
                      </select>
                    ) : (
                      <p
                        className="text-xs text-gray-500 cursor-pointer hover:text-purple-400 transition-colors"
                        onClick={() => setEditingRelationship(member.uuid)}
                      >
                        {member.relationship ? formatRelationship(member.relationship) : 'Set relationship'}
                        {member.familyRole === 'owner' && ((member.relationship ? ' \u00B7 ' : '') + 'Owner')}
                      </p>
                    )}
                  </div>

                  {/* Status badge */}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusStyle(member.memberStatus)}`}>
                    {member.memberStatus}
                  </span>

                  {/* Owner actions (not for self) */}
                  {isOwner && !member.isCurrentUser && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => setRoleChangeTarget(member)}
                        className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
                        title={member.familyRole === 'owner' ? 'Demote to member' : 'Promote to owner'}
                      >
                        {member.familyRole === 'owner' ? (
                          <Crown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Crown className="w-4 h-4 text-purple-400" />
                        )}
                      </button>
                      <button
                        onClick={() => setRemoveMemberTarget(member)}
                        className="p-1.5 hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Remove member"
                      >
                        <UserMinus className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Role change confirmation */}
          {roleChangeTarget && (
            <div className="bg-gray-700/50 border border-gray-600/50 rounded-xl p-4 mt-3">
              <p className="text-sm text-gray-300 mb-3">
                {roleChangeTarget.familyRole === 'owner'
                  ? `Remove ${roleChangeTarget.name}'s owner role?`
                  : `Make ${roleChangeTarget.name} an owner?`}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setRoleChangeTarget(null)}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleToggleMemberRole(roleChangeTarget)}
                  disabled={saving}
                  className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm text-white font-medium disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Updating...' : 'Confirm'}
                </button>
              </div>
            </div>
          )}

          {/* Remove member confirmation */}
          {removeMemberTarget && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 mt-3">
              <p className="text-sm text-red-300 mb-3">
                Remove {removeMemberTarget.name} from {family.name}?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setRemoveMemberTarget(null)}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemoveMember(removeMemberTarget)}
                  disabled={saving}
                  className="flex-1 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white font-medium disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3 pt-4">
          {/* Leave Family (for members, or owners who are not the sole member) */}
          {(!isOwner || (isOwner && !canDeleteFamily)) && (
            !showLeaveConfirm ? (
              <button
                onClick={() => setShowLeaveConfirm(true)}
                className="flex items-center justify-center gap-2 w-full py-3 bg-gray-800 hover:bg-red-900/30 rounded-xl text-red-400 text-sm font-medium transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Leave Family
              </button>
            ) : (
              <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                <p className="text-sm text-red-300 mb-3">
                  If you leave the family you will lose access to the family feed and the family memories.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowLeaveConfirm(false)}
                    className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLeaveFamily}
                    disabled={saving}
                    className="flex-1 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white font-medium disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Leaving...' : 'Leave'}
                  </button>
                </div>
              </div>
            )
          )}

          {/* Delete Family (owner only, sole member only) */}
          {canDeleteFamily && (
            !showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center justify-center gap-2 w-full py-3 bg-gray-800 hover:bg-red-900/30 rounded-xl text-red-400 text-sm font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Family
              </button>
            ) : (
              <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                <p className="text-sm text-red-300 mb-3">
                  Deleting a family is permanent and will destroy all family associated memories and data.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteFamily}
                    disabled={saving}
                    className="flex-1 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white font-medium disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Deleting...' : 'Delete Family'}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default FamilyProfileView;
